import { ReactNode, Suspense, useEffect } from "react";
import { useParams, Outlet } from "@tanstack/react-router";
import { useAuth } from "@/lib/Auth/useAuth";
import { useOptionalUser } from "@/lib/Auth/useUser";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useActiveOrganisation } from "@/app/Organisations/Hooks/useActiveOrganisation";
import * as Sentry from "@sentry/react";
import { DialogProvider } from "@/lib/Components/Dialog/Providers/DialogProvider";
import * as Portal from "@radix-ui/react-portal";
import { Toaster } from "@/components/ui/sonner";
import { AccountVerificationWarning } from "@/app/Meta/Components/AccountVerificationWarning";
import { EmailVerificationBanner } from "@/app/Auth/Components/EmailVerificationBanner";
import { CompleteProfileBanner } from "@/app/Auth/Components/CompleteProfileBanner";
import { OverdueInvoicesWarning } from "@/app/Meta/Components/OverdueInvoicesWarning";
import { OverdueInvoicesModal } from "@/app/Meta/Components/OverdueInvoicesModal";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { metaQuery } from "@/app/Meta/GraphQL/metaQuery";
import { FeatureFlagType } from "@/gql/graphql";
import { useFeatureFlag } from "@/app/Meta/utils/useFeatureFlag";
import { GenericErrorBoundary } from "@/lib/Components/Layout/ErrorBoundary/ErrorBoundary";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { AppShell } from "../Sidebar/AppShell";
import { VehicleDrawer } from "@/app/Vehicles/Components/Drawer/VehicleDrawer";
import { OfficeDrawer } from "@/app/Offices/Components/Drawer/OfficeDrawer";
import { RelocationPageDrawer } from "@/app/Settings/Admin/RelocationPages/Drawer/RelocationPageDrawer";
import { ContactDrawer } from "@/app/Contacts/Components/Drawer/ContactDrawer";
import { ProDriverDrawer } from "@/app/ProDrivers/Drivers/Components/Drawer/ProDriverDrawer";
import { EchoProvider } from "@/lib/Echo/EchoProvider";
import { useThreadActivitySync } from "@/app/Threads/hooks/useThreadActivitySync";

export function AppLayout() {
  const { data } = useGqlQuery(metaQuery, {});
  const isModalEnabled = useFeatureFlag(
    FeatureFlagType.BlockWhenInvoiceOverdue,
  );
  const hasOverdueInvoices = (data?.meta.overdue_invoice_count ?? 0) > 0;

  return (
    <OrganizationGuard>
      <EchoProvider>
        <SentryUserTracker />
        <ThreadActivitySync />
        <DialogProvider>
          <AppShell>
            <EmailVerificationBanner />
            <CompleteProfileBanner />
            <AccountVerificationWarning />
            <OverdueInvoicesWarning />
            <GenericErrorBoundary>
              <Suspense
                fallback={
                  <div className="flex h-full w-full items-center justify-center">
                    <Spinner className="size-6 text-blue-400" />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </GenericErrorBoundary>
            <Portal.Root>
              <Toaster />

              <Suspense fallback={null}>
                <VehicleDrawer />
              </Suspense>
              <Suspense fallback={null}>
                <OfficeDrawer />
              </Suspense>
              <Suspense fallback={null}>
                <RelocationPageDrawer />
              </Suspense>
              <Suspense fallback={null}>
                <ContactDrawer />
              </Suspense>
              <Suspense fallback={null}>
                <ProDriverDrawer />
              </Suspense>

              {isModalEnabled && hasOverdueInvoices ? (
                <OverdueInvoicesModal
                  isOpen={!!data?.meta.overdue_invoice_count}
                />
              ) : null}
            </Portal.Root>
          </AppShell>
        </DialogProvider>
      </EchoProvider>
    </OrganizationGuard>
  );
}

function ThreadActivitySync() {
  useThreadActivitySync();
  return null;
}

function SentryUserTracker() {
  // Non-suspense reads only: this just sets Sentry tags and must never block the
  // app shell. /me loads via useOptionalUser; supplierId comes straight from the
  // URL (no supplier-list fetch).
  const user = useOptionalUser();
  const { isAdmin, tenantType } = useTenant();
  const { supplierId } = useParams({ strict: false }) as {
    supplierId?: string;
  };

  useEffect(() => {
    if (!user) return;

    Sentry.setUser({
      email: user.email,
      id: user.id,
      username: user.name || undefined,
      ip_address: "{{auto}}",
    });

    Sentry.setTag("tenant_type", tenantType);
    Sentry.setTag("is_admin", isAdmin ? "true" : "false");

    if (supplierId) {
      Sentry.setTag("supplier_id", supplierId);
    }
  }, [user, tenantType, isAdmin, supplierId]);

  return null;
}

function OrganizationGuard({ children }: { children: ReactNode }) {
  const { isSignedIn } = useAuth();
  const { activeOrganisationId } = useActiveOrganisation();

  if (!isSignedIn || !activeOrganisationId) {
    return null;
  }

  return children;
}
